import { createContext, useContext, ReactNode } from 'react';
import { useContainerWidth } from './ContainerWidthProvider';
import { theme } from 'rwl-ui';

interface Breakpoints {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
}

const breakpoints: Breakpoints = theme.breakpoints as Breakpoints;

interface BreakpointContextValue {
    isAboveBreakpoint: (key: keyof Breakpoints) => boolean;
    currentBreakpoint: keyof Breakpoints;
    isBelowBreakpoint: (key: keyof Breakpoints) => boolean;
    isBreakpoint: (key: keyof Breakpoints) => boolean;
    isWithin: (min: keyof Breakpoints, max: keyof Breakpoints) => boolean;
}

const BreakpointContext = createContext<BreakpointContextValue | undefined>(
    undefined
);

export const BreakpointProvider = ({ children }: { children: ReactNode }) => {
    const { width } = useContainerWidth();

    const getCurrentBreakpoint = (): keyof Breakpoints => {
        if (width < breakpoints.xs) return 'xs';
        if (width < breakpoints.sm) return 'sm';
        if (width < breakpoints.md) return 'md';
        if (width < breakpoints.lg) return 'lg';
        return 'xl';
    };

    const isBreakpoint = (key: keyof Breakpoints) => {
        const breakpointValue = breakpoints[key];
        return (
            width >= breakpointValue &&
            width <
                breakpoints[
                    Object.keys(breakpoints).find(
                        (k) =>
                            breakpoints[k as keyof Breakpoints] >
                            breakpointValue
                    ) as keyof Breakpoints
                ]
        );
    };

    const isBelowBreakpoint = (key: keyof Breakpoints) => {
        return width < breakpoints[key];
    };

    const isAboveBreakpoint = (key: keyof Breakpoints) => {
        return width >= breakpoints[key];
    };

    const isWithin = (min: keyof Breakpoints, max: keyof Breakpoints) => {
        return width >= breakpoints[min] && width < breakpoints[max];
    };

    return (
        <BreakpointContext.Provider
            value={{
                isAboveBreakpoint,
                currentBreakpoint: getCurrentBreakpoint(),
                isBelowBreakpoint,
                isBreakpoint,
                isWithin,
            }}
        >
            {children}
        </BreakpointContext.Provider>
    );
};

export const useBreakpoint = () => {
    const context = useContext(BreakpointContext);

    if (!context) {
        throw new Error(
            'useBreakpoint must be used within a BreakpointProvider'
        );
    }

    return context;
};
